import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, Search, FileText, Youtube, Link, Eye, ArrowLeft, Upload, Loader2, Download, File } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/api/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContentList = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [content, setContent] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourse, setFilterCourse] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type: 'pdf',
    content_url: '',
    course_id: '',
    subject_id: '',
    order_index: 0
  });

  useEffect(() => {
    fetchData();
  }, [filterCourse, filterType]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contentRes, coursesRes] = await Promise.all([
        api.get('/lms/content', { 
          params: { 
            course_id: filterCourse !== 'all' ? filterCourse : undefined,
            content_type: filterType !== 'all' ? filterType : undefined
          } 
        }),
        api.get('/lms/teacher/courses')
      ]);
      
      setContent(contentRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async (courseId) => {
    if (!courseId) {
      setSubjects([]);
      return;
    }
    try {
      const response = await api.get('/lms/teacher/subjects', { params: { course_id: courseId } });
      setSubjects(response.data || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('File size must be less than 50MB');
        return;
      }
      setSelectedFile(file);
      // Auto-fill title from filename if empty
      if (!formData.title) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setFormData({...formData, title: nameWithoutExt});
      }
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) return null;
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedFile);
      formDataUpload.append('content_type', formData.content_type);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`/lms/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const data = await response.json();
      return data.file_url;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload file');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.course_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      let contentUrl = formData.content_url;
      
      // Upload file if selected (for PDF and document types)
      if (selectedFile && (formData.content_type === 'pdf' || formData.content_type === 'document')) {
        const uploadedUrl = await uploadFile();
        if (uploadedUrl) {
          contentUrl = uploadedUrl;
        } else if (!formData.content_url) {
          toast.error('File upload failed. Please try again or provide a URL.');
          return;
        }
      }
      
      const submitData = { ...formData, content_url: contentUrl };
      
      if (editingContent) {
        await api.put(`/lms/content/${editingContent.id}`, submitData);
        toast.success('Content updated successfully');
      } else {
        await api.post('/lms/content', submitData);
        toast.success('Content created successfully');
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error(error.response?.data?.detail || 'Failed to save content');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    try {
      await api.delete(`/lms/content/${id}`);
      toast.success('Content deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Failed to delete content');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content_type: 'pdf',
      content_url: '',
      course_id: '',
      subject_id: '',
      order_index: 0
    });
    setEditingContent(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openEditModal = (item) => {
    setEditingContent(item);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      content_type: item.content_type || 'pdf',
      content_url: item.content_url || '',
      course_id: item.course_id || '',
      subject_id: item.subject_id || '',
      order_index: item.order_index || 0
    });
    if (item.course_id) fetchSubjects(item.course_id);
    setIsModalOpen(true);
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'youtube': return <Youtube className="text-red-500" size={20} />;
      case 'link': return <Link className="text-blue-500" size={20} />;
      case 'pdf': return <FileText className="text-red-600" size={20} />;
      case 'document': return <File className="text-blue-600" size={20} />;
      default: return <FileText className="text-gray-500" size={20} />;
    }
  };

  const isDownloadableFile = (url) => {
    if (!url) return false;
    // Cloudinary URLs or any https:// URLs are directly downloadable
    return url.startsWith('https://') || url.startsWith('http://');
  };

  const filteredContent = content.filter(item =>
    item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="lms-content-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/lms')} size="sm">
            <ArrowLeft size={18} className="mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Course Content</h1>
            <p className="text-gray-600">Manage videos, PDFs, and learning materials</p>
          </div>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-content-btn"
        >
          <Plus size={18} className="mr-2" /> Add Content
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
                data-testid="search-input"
              />
            </div>
            <Select value={filterCourse} onValueChange={setFilterCourse}>
              <SelectTrigger className="w-[200px]" data-testid="filter-course">
                <SelectValue placeholder="Filter by Course" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                {courses.map(course => (
                  <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[150px]" data-testid="filter-type">
                <SelectValue placeholder="Content Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="youtube">YouTube</SelectItem>
                <SelectItem value="document">Document</SelectItem>
                <SelectItem value="link">Link</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Content List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredContent.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">No content found. Add your first content!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredContent.map((item) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow" data-testid={`content-item-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      {getContentIcon(item.content_type)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{item.title}</h3>
                      <p className="text-sm text-gray-500 line-clamp-1">{item.description || 'No description'}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{item.content_type}</span>
                        <span><Eye size={12} className="inline mr-1" />{item.views || 0} views</span>
                        <span>Created by {item.created_by_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.content_url && (
                      <>
                        {isDownloadableFile(item.content_url) ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => window.open(item.content_url, '_blank')}
                            data-testid={`download-${item.id}`}
                          >
                            <Download size={16} className="mr-1" /> Download
                          </Button>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => window.open(item.content_url, '_blank')}
                          >
                            <Eye size={16} className="mr-1" /> View
                          </Button>
                        )}
                      </>
                    )}
                    <Button variant="outline" size="sm" onClick={() => openEditModal(item)}>
                      <Edit size={16} />
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingContent ? 'Edit Content' : 'Add New Content'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Content title"
                required
                data-testid="content-title-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Course *</label>
                <Select 
                  value={formData.course_id} 
                  onValueChange={(val) => { 
                    setFormData({...formData, course_id: val, subject_id: ''}); 
                    fetchSubjects(val); 
                  }}
                >
                  <SelectTrigger data-testid="course-select">
                    <SelectValue placeholder="Select Course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Subject</label>
                <Select 
                  value={formData.subject_id || 'none'} 
                  onValueChange={(val) => setFormData({...formData, subject_id: val === 'none' ? '' : val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Subject" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific subject</SelectItem>
                    {subjects.map(subj => (
                      <SelectItem key={subj.id} value={subj.id}>{subj.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Content Type *</label>
                <Select 
                  value={formData.content_type} 
                  onValueChange={(val) => setFormData({...formData, content_type: val})}
                >
                  <SelectTrigger data-testid="content-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF Document</SelectItem>
                    <SelectItem value="youtube">YouTube Video</SelectItem>
                    <SelectItem value="document">Word/Other Doc</SelectItem>
                    <SelectItem value="link">External Link</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Order</label>
                <Input
                  type="number"
                  value={formData.order_index}
                  onChange={(e) => setFormData({...formData, order_index: parseInt(e.target.value) || 0})}
                  placeholder="Display order"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">
                {formData.content_type === 'youtube' ? 'YouTube URL' : 'Content URL/Link'}
              </label>
              <Input
                value={formData.content_url}
                onChange={(e) => setFormData({...formData, content_url: e.target.value})}
                placeholder={formData.content_type === 'youtube' ? 'https://youtube.com/watch?v=...' : 'https://...'}
                data-testid="content-url-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.content_type === 'youtube' 
                  ? 'Paste YouTube video URL' 
                  : 'Paste document link or upload file below'}
              </p>
            </div>
            
            {/* File Upload - Only for PDF and Document types */}
            {(formData.content_type === 'pdf' || formData.content_type === 'document') && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Upload className="text-gray-400" size={24} />
                    <div>
                      <p className="font-medium text-sm">
                        {selectedFile ? selectedFile.name : 'Upload Document'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {selectedFile 
                          ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` 
                          : 'PDF, DOCX, XLSX, PPT (Max 50MB)'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {selectedFile ? 'Change' : 'Browse'}
                    </Button>
                    {selectedFile && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        className="text-red-600"
                        onClick={() => {
                          setSelectedFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                  onChange={handleFileChange}
                />
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700" 
                data-testid="save-content-btn"
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>{editingContent ? 'Update' : 'Create'} Content</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentList;
